<template>
  <div class="h-full flex overflow-hidden bg-white">
    <transition
      enter-active-class="transition-opacity ease-linear duration-300"
      leave-active-class="transition-opacity ease-linear duration-300"
      enter-class="opacity-0"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div class="md:hidden" v-if="menuOpen">
        <div class="fixed inset-0 flex z-40">
          <div class="fixed inset-0 z-40">
            <div
              class="absolute inset-0 bg-gray-600 opacity-75 z-40"
              @click.self="toggleMenu"
            ></div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. -->
    <transition
      enter-active-class="transition ease-in-out duration-300 transform"
      leave-active-class="transition ease-in-out duration-300 transform"
      enter-class="-translate-x-full"
      enter-to-class="translate-x-0"
      leave-class="translate-x-0"
      leave-to-class="-translate-x-full"
    >
      <div class="md:hidden" v-if="menuOpen">
        <div class="fixed inset-0 flex z-40">
          <div
            class="relative flex-1 flex flex-col max-w-xs w-full bg-white"
            v-if="menuOpen"
          >
            <div class="absolute top-0 right-0 -mr-12 pt-2">
              <button
                class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                v-on:click="toggleMenu"
              >
                <span class="sr-only">Close sidebar</span>
                <!-- Heroicon name: outline/x -->
                <svg
                  class="h-6 w-6 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div class="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div class="flex-shrink-0 flex items-center px-4">
                <h1 class="text-xl font-bold">Live Storms Media</h1>
              </div>
              <div>
                <div
                  class="hidden bg-gray-100 p-1 rounded-lg items-center sm:flex text-sm"
                >
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-gray-400 hover:bg-white hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                  >
                    Media
                  </button>
                  <button
                    type="button"
                    class="ml-0.5 bg-blue-600 p-1.5 rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                  >
                    Upload
                  </button>
                  <button
                    type="button"
                    class="ml-0.5 bg-blue-600 p-1.5 rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                  >
                    Stringer
                  </button>
                </div>
              </div>
              <AdminNav />
            </div>
          </div>
          <div class="flex-shrink-0 w-14">
            <!-- Force sidebar to shrink to fit close icon -->
          </div>
        </div>
      </div>
    </transition>

    <!-- Static sidebar for desktop -->
    <div
      class="hidden md:flex md:flex-shrink-0 z-40"
      @mouseover="expand = true"
      @mouseleave="expand = false"
    >
      <div
        class="fixed inset-y-0 flex flex-col transition-all"
        :class="{ 'w-72': menuOpen || expand, 'w-20': !menuOpen }"
      >
        <!-- Sidebar component, swap this element with another sidebar if you like -->
        <div class="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
          <div
            class="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto space-y-4 relative"
          >
            <div
              class="flex items-center flex-shrink-0 px-4"
              :class="{ 'justify-center': !menuOpen && !expand }"
            >
              <h1
                class="text-xl font-bold text-center"
                v-if="!menuOpen && !expand"
              >
                LSM
              </h1>
              <h1
                class="text-xl font-bold overflow-hidden whitespace-nowrap"
                v-if="menuOpen || expand"
              >
                Live Storms Media
              </h1>
              <div
                class="absolute right-0 top-0 mt-4 mr-5 flex items-center content-center justify-center bg-gray-100 w-8 h-8 rounded-full cursor-pointer"
                v-on:click="toggleMenu"
                v-if="menuOpen || expand"
              >
                <i class="fa-solid fa-thumbtack" v-if="menuOpen"></i>
                <i class="fa-regular fa-thumbtack" v-if="!menuOpen"></i>
              </div>
            </div>
            <AdminNav :collapsed="!menuOpen && !expand" />
          </div>
          <AdminNavBottom :collapsed="!menuOpen && !expand" />
        </div>
      </div>
    </div>

    <div
      class="flex flex-col min-w-0 flex-1 overflow-y-auto bg-gray-100"
      :class="{ 'pl-72': menuOpen, 'md:pl-20': !menuOpen }"
    >
      <div class="md:hidden">
        <div
          class="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-4 py-1.5"
        >
          <div class="text-xl font-bold">
            Live Storms Media
          </div>
          <div>
            <button
              type="button"
              class="-mr-3 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600"
              v-on:click="toggleMenu"
            >
              <span class="sr-only">Open sidebar</span>
              <!-- Heroicon name: outline/menu -->
              <svg
                class="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <router-view></router-view>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AdminNav from "@/screens/admin/partials/Nav";
import AdminNavBottom from "@/screens/admin/partials/NavBottom";

export default {
  name: "AdminLayout",
  data() {
    return {
      menuOpen: true,
      showVideoPopover: false,
      expand: true
    };
  },
  components: { AdminNav, AdminNavBottom },
  computed: {
    ...mapGetters(["userInfo"])
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    handleScroll() {
      this.$eventBus.$emit("scroll");
    },
    toggleVideoPopver() {
      this.showVideoPopover = !this.showVideoPopover;
    }
  }
};
</script>
<style>
.preventiOSOverscrolling {
  -webkit-overflow-scrolling: touch;
}
</style>
